import { assign, createMachine } from 'xstate';
import { ProductType } from '@tink/core/src/types';
import { ErrorReason, ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { credentialsService, CredentialsService } from '../../services/credentialsService';
import { ProviderEntity } from '../../../models/provider';
import { CredentialsEntity } from '../../../models/credentials';
import {
  ConsumedHandoffSession,
  HandoffCredentialsAction,
  HandoffSession,
} from '../../../models/handoffSession';
import { providersService, ProvidersService } from '../../../services/providersService';
import { handoffSessionService, HandoffSessionService } from '../../services/handoffSessionService';
import { ApplicationError } from '../../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { ProductNames } from '../../../models/product';
import { RefreshableItem } from '../../../models/refreshableItem';
import { getProviderRefreshableItems } from '../../../utilities/provider-utils';
import { INVALID_HANDOFF_ACTION_ERROR } from '../handoffCreatorMachine/handoffCreatorMachine';
import { CommonError } from '../../../stores/app-state-errors';
import { AuthenticationOption } from '../../services/credentialsClient';
import { Typegen0 } from './handoffConsumerMachine.typegen';

export type HandoffConsumerMachineContext = {
  handoffSession: ConsumedHandoffSession;
  credentialsAction: HandoffCredentialsAction;
  credentialsIdFromTPP: string | undefined;
  provider?: ProviderEntity;
  credentials?: CredentialsEntity;
  error?: ApplicationError;
};

export type HandoffConsumerMachineEvents = {
  type: 'MACHINE_DONE_EVENT';
  credentials: CredentialsEntity;
};

export type HandoffConsumerMachineServices = {
  getProvider: { data: ProviderEntity };
  createCredentials: { data: CredentialsEntity };
  updateCredentials: { data: CredentialsEntity };
  refreshCredentials: { data: null };
  authenticateCredentials: { data: null };
  getCredentials: { data: CredentialsEntity };
  updateHandoffSession: { data: HandoffSession };
};

export type HandoffConsumerMachineArgs = {
  initialContext: HandoffConsumerMachineContext;
  credentialsId: string | undefined;
  productType: ProductType;
  productNames: ProductNames[];
  reportRefreshItems: RefreshableItem[] | undefined;
  selectedAuthenticationOptions: AuthenticationOption[] | undefined;
  triggerRefresh: boolean;
  scope: string;
  refreshableItems: RefreshableItem[] | undefined;
  authenticate: boolean;
};

export type HandoffConsumerMachineTestOverrides = {
  credentialService?: CredentialsService;
  providerService?: ProvidersService;
  handoffService?: HandoffSessionService;
  initialState?: Exclude<Typegen0['matchesStates'], object>;
};

export const ERROR_INVALID_HANDOFF_PARAMETER: CommonError = {
  errorStatus: ErrorStatus.BAD_REQUEST,
  errorReason: ErrorReason.INVALID_PARAMETER_HANDOFF_URL,
};

export const createHandoffConsumerMachine = ({
  initialContext,
  credentialsId,
  reportRefreshItems,
  refreshableItems = [],
  triggerRefresh,
  productType,
  productNames,
  scope,
  authenticate,
  selectedAuthenticationOptions,
  initialState = 'INITIAL_PARAMS',
  handoffService = handoffSessionService,
  credentialService = credentialsService,
  providerService = providersService,
}: HandoffConsumerMachineArgs & HandoffConsumerMachineTestOverrides) =>
  createMachine(
    {
      id: 'HandoffConsumerMachine',
      context: initialContext,
      initial: initialState,
      tsTypes: {} as import('./handoffConsumerMachine.typegen').Typegen0,
      schema: {
        context: {} as HandoffConsumerMachineContext,
        services: {} as HandoffConsumerMachineServices,
        events: {} as HandoffConsumerMachineEvents,
      },
      predictableActionArguments: true,
      preserveActionOrder: true,
      states: {
        GET_PROVIDER: {
          invoke: {
            src: 'getProvider',
            onDone: [
              {
                target: 'CHOOSE_CREDENTIALS_OPERATION',
                actions: 'set_provider_in_context',
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
        CHOOSE_CREDENTIALS_OPERATION: {
          always: [
            {
              target: 'CREATE_CREDENTIALS',
              cond: 'check_if_credentials_operation_create',
            },
            {
              target: 'REFRESH_CREDENTIALS',
              cond: 'check_if_credentials_operation_refresh',
            },
            {
              target: 'AUTHENTICATE_CREDENTIALS',
              cond: 'check_if_credentials_operation_authenticate',
            },
            {
              target: 'UPDATE_CREDENTIALS',
              cond: 'check_if_credentials_operation_update',
            },
            {
              target: 'GET_CONSUMED_CREDENTIALS',
              cond: 'check_if_credentials_operation_bank_id',
            },
            {
              target: 'APPLICATION_ERROR',
              actions: 'throw_invalid_credentials_operation_error',
            },
          ],
        },
        CREATE_CREDENTIALS: {
          invoke: {
            src: 'createCredentials',
            onDone: [
              {
                target: 'UPDATE_HANDOFF_SESSION',
                actions: 'set_credentials_in_context',
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
        UPDATE_CREDENTIALS: {
          invoke: {
            src: 'updateCredentials',
            onDone: [
              {
                target: 'UPDATE_HANDOFF_SESSION',
                actions: 'set_credentials_in_context',
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
        REFRESH_CREDENTIALS: {
          invoke: {
            src: 'refreshCredentials',
            onDone: [
              {
                target: 'GET_CREDENTIALS',
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
        AUTHENTICATE_CREDENTIALS: {
          invoke: {
            src: 'authenticateCredentials',
            onDone: [
              {
                target: 'GET_CREDENTIALS',
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
        GET_CREDENTIALS: {
          invoke: {
            src: 'getCredentials',
            onDone: [
              {
                target: 'UPDATE_HANDOFF_SESSION',
                actions: 'set_credentials_in_context',
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
        UPDATE_HANDOFF_SESSION: {
          invoke: {
            src: 'updateHandoffSession',
            onDone: [
              {
                target: 'CREDENTIALS_MACHINE',
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
        APPLICATION_ERROR: {
          type: 'final',
        },
        INITIAL_PARAMS: {
          always: [
            {
              target: 'APPLICATION_ERROR',
              cond: 'check_if_initial_params_invalid',
              actions: 'throw_invalid_parameters_error',
            },
            {
              target: 'CREDENTIALS_MACHINE',
              cond: 'check_if_credentials_from_tpp_exist',
            },
            {
              target: 'GET_CONSUMED_CREDENTIALS',
              cond: 'check_if_handoff_session_was_consumed_and_contains_credentials_id',
            },
            {
              target: 'CHOOSE_CREDENTIALS_OPERATION',
              cond: 'check_if_provider_exists',
            },
            {
              target: 'GET_PROVIDER',
            },
          ],
        },
        HANDOFF_COMPETED: {
          type: 'final',
        },
        CREDENTIALS_ERROR: {
          type: 'final',
        },
        CREDENTIALS_MACHINE: {
          on: {
            MACHINE_DONE_EVENT: {
              target: 'HANDOFF_COMPETED',
              actions: 'update_credentials_in_context',
            },
          },
        },
        GET_CONSUMED_CREDENTIALS: {
          invoke: {
            src: 'getCredentials',
            onDone: [
              {
                target: 'CREDENTIALS_MACHINE',
                actions: 'set_credentials_in_context',
              },
            ],
            onError: [
              {
                target: 'APPLICATION_ERROR',
                actions: 'set_error_in_context',
              },
            ],
          },
        },
      },
    },
    {
      guards: {
        check_if_credentials_operation_create: ({ credentialsAction }) =>
          credentialsAction === HandoffCredentialsAction.ADD_CREDENTIALS,
        check_if_credentials_operation_refresh: ({ credentialsAction }) =>
          credentialsAction === HandoffCredentialsAction.REFRESH_CREDENTIALS,
        check_if_credentials_operation_authenticate: ({ credentialsAction }) =>
          credentialsAction === HandoffCredentialsAction.AUTHENTICATE_CREDENTIALS,
        check_if_credentials_operation_bank_id: ({ credentialsAction }) =>
          credentialsAction === HandoffCredentialsAction.BANKID_AUTH,
        check_if_credentials_operation_update: ({ credentialsAction }) =>
          credentialsAction === HandoffCredentialsAction.UPDATE_CREDENTIALS_CONSENT,
        check_if_initial_params_invalid: ({ credentialsAction, handoffSession }) => {
          switch (credentialsAction) {
            case HandoffCredentialsAction.NONE:
              return true;
            case HandoffCredentialsAction.BANKID_AUTH: {
              return !handoffSession.credentialsId;
            }
            case HandoffCredentialsAction.UPDATE_CREDENTIALS_CONSENT:
            case HandoffCredentialsAction.AUTHENTICATE_CREDENTIALS:
            case HandoffCredentialsAction.REFRESH_CREDENTIALS: {
              return !credentialsId;
            }
            default:
              return !handoffSession.url;
          }
        },
        check_if_credentials_from_tpp_exist: ({ credentialsIdFromTPP }) => !!credentialsIdFromTPP,
        check_if_handoff_session_was_consumed_and_contains_credentials_id: ({ handoffSession }) =>
          !!handoffSession.credentialsId && handoffSession.isConsumed,
        check_if_provider_exists: ({ provider }) => !!provider,
      },
      actions: {
        set_provider_in_context: assign({ provider: (_, event) => event.data }),
        set_credentials_in_context: assign({ credentials: (_, event) => event.data }),
        update_credentials_in_context: assign({ credentials: (_, event) => event.credentials }),
        set_error_in_context: assign({ error: (_, event) => event.data as ApplicationError }),
        throw_invalid_credentials_operation_error: assign({
          error: (_) => new ApplicationError(INVALID_HANDOFF_ACTION_ERROR),
        }),
        throw_invalid_parameters_error: assign({
          error: (_) => new ApplicationError(ERROR_INVALID_HANDOFF_PARAMETER),
        }),
      },
      services: {
        getProvider: ({ handoffSession }) =>
          providerService.fetchProvider(handoffSession.providerName),
        createCredentials: ({ handoffSession, provider }) =>
          credentialService.createCredentials(
            {
              productNames,
              providerName: handoffSession.providerName,
              appUri: handoffSession.url,
              fields: {},
              triggerRefresh,
              selectedAuthenticationOptions,
            },
            reportRefreshItems ||
              getProviderRefreshableItems({
                provider: provider!,
                productType,
                scope,
                refreshableItems,
              })
          ),
        updateCredentials: ({ handoffSession }) =>
          credentialService.updateCredentials(credentialsId!, {
            productNames,
            providerName: handoffSession.providerName,
            appUri: handoffSession.url,
            fields: {},
            triggerRefresh,
            selectedAuthenticationOptions,
          }),
        refreshCredentials: ({ handoffSession, provider }) =>
          credentialService.refreshCredentials({
            refreshableItems:
              reportRefreshItems ||
              getProviderRefreshableItems({
                provider: provider!,
                productType,
                scope,
                refreshableItems,
              }),
            id: credentialsId!,
            request: {
              productNames,
              appUri: handoffSession.url,
            },
            authenticate,
          }),
        authenticateCredentials: ({ handoffSession }) =>
          credentialService.authenticateCredentials({
            id: credentialsId!,
            request: { productNames, appUri: handoffSession.url },
          }),
        getCredentials: ({ handoffSession, credentialsIdFromTPP }) =>
          credentialService.getCredentials(
            handoffSession.credentialsId || credentialsIdFromTPP || credentialsId!
          ),
        updateHandoffSession: ({ credentials, handoffSession }) =>
          handoffService.assignCredentials({
            credentialsId: credentials?.id || credentialsId!,
            handoffSessionId: handoffSession.id,
          }),
      },
    }
  );
